import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { OfficialDispatchInjection } from "features/officialDispatch/di/OfficialDispatchInjection";
import { OfficialDispatchRequestEntity } from "features/officialDispatch/domain/entities/OfficialDispatchRequestEntity";
import { OfficialDispatchUseCase } from "features/officialDispatch/domain/useCases/OfficialDispatchUseCase";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";
import { IOfficialDispatchObservedState } from "../interfaces/IOfficialDispatch";

// ─── Input ────────────────────────────────────────────────────────────────────

class OfficialDispatchInput implements Disposable {
  public fetchDispatch = new BehaviorSubject<OfficialDispatchRequestEntity>({});
  public fetchServices = new BehaviorSubject<any>({});

  [Symbol.dispose](): void {
    this.fetchDispatch.complete();
    this.fetchServices.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────

class OfficialDispatchOutput implements Disposable {
  public onError = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.onError.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class OfficialDispatchViewModel extends BasedObservedViewModel<
  OfficialDispatchInput,
  OfficialDispatchOutput,
  IOfficialDispatchObservedState
> {
  private useCase: OfficialDispatchUseCase = OfficialDispatchInjection.getUseCase();

  protected override createObservedState(): IOfficialDispatchObservedState {
    return {
      loading: false,
      list: [],
      serviceOptions: [],
    };
  }

  constructor() {
    super(new OfficialDispatchInput(), new OfficialDispatchOutput());
  }

  protected override transform(): void {
    // Fetch official dispatches
    this.skipFirstInitial(this.input.fetchDispatch, async (params) => {
      try {
        this.setObservedState({ loading: true, list: [] });
        const res = await this.useCase.getOfficialDispatches(params);
        if (res?.data) {
          this.setObservedState({ list: res.data });
        }
      } catch (error: any) {
        console.error(error);
        this.setObservedState({ list: [] });
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    // Fetch services
    this.skipFirstInitial(this.input.fetchServices, async () => {
      try {
        const res = await this.useCase.getServiceList();
        if (res) {
          this.setObservedState({ serviceOptions: res });
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      }
    });
  }
}
